import {useQuery} from "@apollo/client";
import {useState} from "react";
import {Helmet} from "react-helmet-async";
import {FiExternalLink} from "react-icons/fi";

import Button from "@/components/button/Button.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Icon} from "@/components/icon/Icon.tsx";
import {TabContentContainer} from "@/components/layout/TabContentContainer.tsx";
import {LoadingContainer} from "@/components/placeholder/LoadingContainer.tsx";
import {Sorting, SortingDirection} from "@/components/table/Table.types.ts";
import {SERVICENOW, WHATSAPP} from "@/lib/featureFlags.ts";
import {UpErrorBoundary} from "@/lib/handlers/UpErrorBoundary.tsx";
import {useTabs} from "@/lib/hooks/useTabs.tsx";
import {GQL_INTEGRATIONS_PAGE_QUERY, IntegrationsPageQueryResponse} from "@/lib/queries/integrations.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {EditIntegrationModalAction} from "@/pages/integrations/edit/EditIntegrationModalAction.tsx";
import {
  IntegrationModuleInfo,
  IntegrationModules,
  isKnownIntegrationModule,
} from "@/pages/integrations/integrations.types.ts";
import {IntegrationsActive} from "@/pages/integrations/list/integrationsActive/IntegrationsActive.tsx";
import {IntegrationsLibrary} from "@/pages/integrations/list/integrationsLibrary/IntegrationsLibrary.tsx";
import {TestIntegrationModal} from "@/pages/integrations/test/TestIntegrationModal.tsx";

enum IntegrationsTabs {
  LIBRARY = "library",
  ACTIVE = "active",
}

export const IntegrationsPage = () => {
  const [newIntegration, setNewIntegration] = useState<IntegrationFormData | null>(null);
  const [editIntegration, setEditIntegration] = useState<IntegrationFormData | null>(null);
  const {userHasFeature} = useCurrentUserContext();
  const {tab, changeTab} = useTabs({
    defaultTab: IntegrationsTabs.LIBRARY,
    tabList: Object.values(IntegrationsTabs),
  });

  const [integrationSortingColAndDirection, setIntegrationSortingColAndDirection] = useState<Sorting>({
    column: "name",
    direction: SortingDirection.Asc,
  });

  const {data, loading, previousData} = useQuery<IntegrationsPageQueryResponse>(GQL_INTEGRATIONS_PAGE_QUERY, {
    variables: {
      sort:
        integrationSortingColAndDirection.direction == SortingDirection.Asc
          ? integrationSortingColAndDirection.column
          : `-${integrationSortingColAndDirection.column}`,
      limit: 6,
    },
  });
  const renderData = loading ? previousData : data;

  if (!renderData) {
    return <LoadingContainer text="Loading..." />;
  }

  const activeModules = renderData ? renderData.integrations.items.map((integration) => integration.module) : [];

  const activeIntegrationsCount = renderData
    ? renderData.integrations.items.filter((integration) => isKnownIntegrationModule(integration.module)).length
    : 0;

  const popularIntegrations = renderData?.popularIntegrations.items ?? [];

  const hiddenModules = new Set<IntegrationModules>();
  if (!userHasFeature(SERVICENOW)) {
    hiddenModules.add(IntegrationModules.SERVICENOW);
  }
  if (!userHasFeature(WHATSAPP)) {
    hiddenModules.add(IntegrationModules.WHATSAPP);
  }

  const visibleModuleCount = Object.keys(IntegrationModuleInfo).length - hiddenModules.size;

  return (
    <TabContentContainer
      sections={{
        [IntegrationsTabs.LIBRARY]: {
          id: IntegrationsTabs.LIBRARY,
          title: "Library",
          badgeText: visibleModuleCount,
          isVisible: true,
        },
        [IntegrationsTabs.ACTIVE]: {
          id: IntegrationsTabs.ACTIVE,
          title: "Active",
          badgeText: renderData ? activeIntegrationsCount : "",
          isVisible: true,
        },
      }}
      title="Integrations"
      tab={tab}
      setTab={changeTab}
      actionsBar={
        <div className="d-flex gap-2">
          <Button variant={"secondary"} size={"large"} as={"a"} href={"https://feedback.uptime.com"} target={"_blank"}>
            Request Integration <Icon icon={FiExternalLink} />
          </Button>
        </div>
      }
    >
      <Helmet>
        <title>Integrations</title>
      </Helmet>
      <UpErrorBoundary>
        {tab === IntegrationsTabs.LIBRARY && (
          <IntegrationsLibrary
            activeModules={activeModules}
            hiddenModules={hiddenModules}
            popularIntegrations={popularIntegrations}
            setNewIntegration={(integration) => {
              setNewIntegration(integration);
            }}
          />
        )}
        {tab === IntegrationsTabs.ACTIVE && (
          <IntegrationsActive
            integrations={renderData?.integrations}
            sortColumn={integrationSortingColAndDirection.column}
            sortDirection={integrationSortingColAndDirection.direction}
            onSortingChange={setIntegrationSortingColAndDirection}
            setTab={changeTab}
          />
        )}
        {!!newIntegration && (
          <TestIntegrationModal
            show={!!newIntegration}
            hide={() => {
              setNewIntegration(null);
            }}
            integration={newIntegration}
            onEdit={() => {
              setEditIntegration(newIntegration);
              setNewIntegration(null);
            }}
          />
        )}
        {!!editIntegration && (
          <EditIntegrationModalAction
            formId={`form-integration`}
            show={!!editIntegration}
            setShow={(show) => {
              if (!show) setEditIntegration(null);
            }}
            integration={editIntegration}
            setNewIntegration={setNewIntegration}
          />
        )}
      </UpErrorBoundary>
    </TabContentContainer>
  );
};
